
import type { AccessTokenData } from '@empathyco/playboard-core-domain'
import type { AppConfig, FeaturesConfig } from '../models'
import type { PopoverItem } from './popover-list.component.vue'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { useDark } from '@vueuse/core'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { mapGetters } from '../composables/utils'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { FeatureIdEnum } from '../enums'
import { ROUTER_CONSTS } from '../router/router.consts'
import { AUTH_CONSTS } from '../store/auth/auth.consts'
import PopoverList from './popover-list.component.vue'
import { EpbButton, EpbSwitch } from './shared'

export default defineComponent({
  name: 'AppHeaderUser',
  components: {
    EpbButton,
    PopoverList,
    EpbSwitch,
  },
  props: {
    collapsed: Boolean,
  },
  setup(props) {
    const ctx = getCurrentInstance()
    const switchMessages = ctx.proxy.$epb.messages.app.switch
    const baseClass = 'epb-app-header-user'
    const classes = computed(() => [baseClass, { [`${baseClass}--collapsed`]: props.collapsed }])

    const { tokenInfo } = mapGetters<AccessTokenData>(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
      tokenInfo: AUTH_CONSTS.getters.tokenInfo,
    })
    const userName = computed<string | undefined>(() => {
      let result: string
      const user = tokenInfo.value?.user
      if (user) {
        const builtName = [user.firstName, user.lastName].filter(value => !!value).join(' ')
        // TODO Remove @ts-ignore once feature flag is available
        // @ts-ignore
        const { oidcLogin } = CONTAINER.get<AppConfig>(TYPES.AppConfig)
        result = oidcLogin ? user.name || builtName : builtName || user.name
      }
      return result
    })

    const isAccountAvailable = computed<boolean>(() =>
      CONTAINER.get<FeaturesConfig>(TYPES.FeaturesConfig)[DomainEnum.GLOBAL].some(
        featureConfig => featureConfig.id === FeatureIdEnum.ACCOUNT,
      ),
    )

    const options = computed<Array<PopoverItem>>(() => {
      const {
        proxy: { $epb, $route },
      } = ctx
      const messages = ctx.proxy.$epb.messages.app
      return [
        ...(isAccountAvailable.value
          ? [
              {
                id: 'user',
                label: messages.features[FeatureIdEnum.ACCOUNT],
                icon: 'usercircular',
                route: {
                  ...$route,
                  name: ROUTER_CONSTS.routes[FeatureIdEnum.ACCOUNT],
                },
                selected: $route.name === ROUTER_CONSTS.routes[FeatureIdEnum.ACCOUNT],
              },
            ]
          : []),
        {
          id: 'book',
          label: messages.user.documentationTitle,
          icon: 'book',
          route: messages.user.documentationLink,
          target: '_blank',
        },
        {
          id: 'darkMode',
          label: messages.darkMode,
          icon: 'personalize',
          isCustom: true,
        },
      ]
    })

    const isDark = useDark({
      selector: 'html',
      attribute: 'data-theme',
    })
    function select(option: PopoverItem): void {
      if (option.target) {
        window.open(option.route as string, option.target)
        return
      }
      ctx.proxy.$router.push(option.route)
    }

    function logout(): void {
      const {
        proxy: { $router, $route },
      } = ctx
      $router.push({
        ...$route,
        name: ROUTER_CONSTS.routes.logout,
      })
    }

    const iconRef = ref<HTMLElement | null>(null)

    function animateChevron(): void {
      iconRef.value.classList.toggle(`${baseClass}__wrapper__right--rotated`)
    }

    return {
      baseClass,
      classes,
      userName,
      options,
      select,
      logout,
      iconRef,
      animateChevron,
      switchMessages,
      isDark,
    }
  },
})
